import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toDisplay = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

// Accepts DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, DD.MM.YYYY
const parseDate = (str) => {
  if (!str) return null;
  const s = str.trim();
  // YYYY-MM-DD
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (m) {
    const d = new Date(+m[1], +m[2] - 1, +m[3]);
    if (d.getFullYear() === +m[1] && d.getMonth() === +m[2] - 1 && d.getDate() === +m[3]) return d;
  }
  // DD/MM/YYYY or DD-MM-YYYY or DD.MM.YYYY
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (m) {
    const d = new Date(+m[3], +m[2] - 1, +m[1]);
    if (d.getFullYear() === +m[3] && d.getMonth() === +m[2] - 1 && d.getDate() === +m[1]) return d;
  }
  return null;
};

const DatePicker = ({ value, onChange, minYear = 1900, maxYear = new Date().getFullYear() + 10, placeholder = 'DD/MM/YYYY' }) => {
  // value is ISO string YYYY-MM-DD
  const selectedDate = value ? parseDate(value) : null;
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState(selectedDate ? toDisplay(selectedDate) : '');
  const [viewMonth, setViewMonth] = useState(selectedDate || new Date());
  const containerRef = useRef(null);

  useEffect(() => {
    const parsed = value ? parseDate(value) : null;
    if (parsed) {
      setInputVal(toDisplay(parsed));
      setViewMonth(parsed);
    } else {
      setInputVal('');
    }
  }, [value]);

  const years = [];
  for (let y = maxYear; y >= minYear; y--) years.push(y);

  const handleInputChange = (e) => {
    setInputVal(e.target.value);
  };

  const commitInput = () => {
    const parsed = parseDate(inputVal);
    if (parsed) {
      onChange(toISO(parsed));
      setViewMonth(parsed);
    } else if (inputVal === '') {
      onChange('');
    } else if (selectedDate) {
      // revert
      setInputVal(toDisplay(selectedDate));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitInput();
      e.target.blur();
    }
  };

  const handleSelect = (d) => {
    if (!d) return;
    onChange(toISO(d));
    setInputVal(toDisplay(d));
    setViewMonth(d);
    setOpen(false);
  };

  const changeMonth = (delta) => {
    const nm = new Date(viewMonth);
    nm.setMonth(nm.getMonth() + delta);
    setViewMonth(nm);
  };

  const setToday = () => {
    const t = new Date();
    handleSelect(t);
  };

  const clear = () => {
    onChange('');
    setInputVal('');
    setOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Input
            type="text"
            value={inputVal}
            onChange={handleInputChange}
            onBlur={commitInput}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="pr-10"
          />
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-md flex items-center justify-center text-slate-500 hover:text-saffron-600 hover:bg-saffron-50 transition-colors"
                aria-label="Open calendar"
              >
                <CalendarIcon className="w-4 h-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start" sideOffset={6}>
              {/* Custom header with month/year selectors */}
              <div className="flex items-center justify-between gap-2 p-3 border-b border-slate-100 bg-slate-50/60">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => changeMonth(-1)}
                  type="button"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex gap-1.5 flex-1">
                  <Select
                    value={String(viewMonth.getMonth())}
                    onValueChange={(v) => {
                      const nm = new Date(viewMonth);
                      nm.setMonth(+v);
                      setViewMonth(nm);
                    }}
                  >
                    <SelectTrigger className="h-8 flex-1 text-sm font-medium">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {MONTHS.map((m, i) => (
                        <SelectItem key={m} value={String(i)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={String(viewMonth.getFullYear())}
                    onValueChange={(v) => {
                      const nm = new Date(viewMonth);
                      nm.setFullYear(+v);
                      setViewMonth(nm);
                    }}
                  >
                    <SelectTrigger className="h-8 w-24 text-sm font-medium">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {years.map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => changeMonth(1)}
                  type="button"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleSelect}
                month={viewMonth}
                onMonthChange={setViewMonth}
                classNames={{
                  caption: 'hidden', // hide default caption since we have custom
                  nav: 'hidden',
                  day_selected: 'bg-saffron-500 text-white hover:bg-saffron-600 hover:text-white focus:bg-saffron-600 focus:text-white',
                  day_today: 'bg-indgreen-100 text-indgreen-700 font-semibold',
                  day: cn(
                    'h-8 w-8 p-0 font-normal rounded-md hover:bg-saffron-50 aria-selected:opacity-100 transition-colors'
                  ),
                  head_cell: 'text-slate-500 rounded-md w-8 font-medium text-[0.7rem] uppercase tracking-wider',
                }}
              />

              <div className="flex items-center justify-between gap-2 p-2 border-t border-slate-100 bg-slate-50/60">
                <Button variant="ghost" size="sm" className="text-xs h-7 text-slate-600" onClick={clear} type="button">
                  Clear
                </Button>
                <Button size="sm" className="text-xs h-7 bg-indgreen-500 hover:bg-indgreen-600 text-white" onClick={setToday} type="button">
                  Today
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
};

export default DatePicker;
